import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { useUser } from '../../UserContext';
import { db } from '../../Firebase';

function MultipleQuiz() {
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizSessionId, setQuizSessionId] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
  const { currentUser } = useUser();
  
  const navigate = useNavigate();

  // Fetch all multiple-choice flashcards
  useEffect(() => {
    const fetchCards = async () => {
      try {
        const q = query(
          collection(db, 'flashcards'),
          where('userId', '==', currentUser?.uid),
          where('answerType', '==', 'multiple')
        );
        const querySnapshot = await getDocs(q);
        const fetchedCards = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          options: Object.entries(doc.data().options)
            .filter(([_, value]) => value) // Remove empty options
            .map(([key, value]) => ({
              id: key,
              text: value
            }))
        }));

        setCards(fetchedCards);
        if (fetchedCards.length > 0) {
          const sessionId = `quiz_${Date.now()}`;
          await setDoc(doc(db, 'quizSessions', sessionId), {
            userId: currentUser.uid,
            startedAt: new Date(),
            totalQuestions: fetchedCards.length,
            quizType: 'multiple'
          });
          setQuizSessionId(sessionId);
        }
      } catch (error) {
        console.error('Error fetching cards:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, [currentUser?.uid, db]);

  // Check if question has already been answered in this session
  const checkExistingAnswer = async (questionId) => {
    try {
      const answerDoc = await getDoc(doc(db, 'quizSessions', quizSessionId, 'answers', questionId));
      return answerDoc.exists() ? answerDoc.data() : null;
    } catch (error) {
      console.error('Error checking existing answer:', error);
      return null;
    }
  };

  // Calculate final score from all answers in Firestore
  const calculateFinalScore = async () => {
    try {
      let totalScore = 0;
      console.log('Calculating final score for', cards.length, 'cards'); // Debug log
      
      for (const card of cards) {
        const answerDoc = await getDoc(doc(db, 'quizSessions', quizSessionId, 'answers', card.id));
        if (answerDoc.exists()) {
          const answerData = answerDoc.data();
          console.log('Answer for card', card.id, ':', answerData); // Debug log
          if (answerData.isCorrect) {
            totalScore += 1;
          }
        } else {
          console.log('No answer found for card', card.id); // Debug log
        }
      }
      
      console.log('Final calculated score:', totalScore); // Debug log
      return totalScore;
    } catch (error) {
      console.error('Error calculating final score:', error);
      return score; // Fallback to current score if calculation fails
    }
  };

  const handleSubmit = async () => {
    if (!selectedOption) return;
    
    const currentCard = cards[currentIndex];
    const isCorrect = selectedOption === currentCard.correctOption;
    setShowFeedback(true);

    // Check if this question has already been answered
    const existingAnswer = await checkExistingAnswer(currentCard.id);
    
    if (existingAnswer) {
      // Update existing answer
      const previousScore = existingAnswer.isCorrect ? 1 : 0;
      const newScore = isCorrect ? 1 : 0;
      const scoreDifference = newScore - previousScore;
      
      await updateDoc(doc(db, 'quizSessions', quizSessionId, 'answers', currentCard.id), {
        question: currentCard.question,
        userAnswer: selectedOption,
        correctAnswer: currentCard.correctOption,
        isCorrect,
        timestamp: new Date(),
        attempts: (existingAnswer.attempts || 1) + 1,
        previousAnswers: [...(existingAnswer.previousAnswers || []), existingAnswer.userAnswer],
        options: currentCard.options.reduce((acc, opt) => {
          acc[opt.id] = opt.text;
          return acc;
        }, {})
      });

      // Update score if it changed
      if (scoreDifference !== 0) {
        setScore(prev => prev + scoreDifference);
      }
    } else {
      // Save new answer
      await setDoc(doc(db, 'quizSessions', quizSessionId, 'answers', currentCard.id), {
        question: currentCard.question,
        userAnswer: selectedOption,
        correctAnswer: currentCard.correctOption,
        isCorrect,
        timestamp: new Date(),
        attempts: 1,
        previousAnswers: [],
        options: currentCard.options.reduce((acc, opt) => {
          acc[opt.id] = opt.text;
          return acc;
        }, {})
      });

      if (isCorrect) {
        setScore(prev => prev + 1);
      }
    }

    // Add to answered questions set
    setAnsweredQuestions(prev => new Set([...prev, currentCard.id]));

    // Move to next question or end quiz
    setTimeout(() => {
      setShowFeedback(false);
      setSelectedOption(null);
      
      if (currentIndex < cards.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        endQuiz();
      }
    }, 1500);
  };

  const endQuiz = async () => {
    // Calculate final score from all answers in Firestore
    const finalScore = await calculateFinalScore();
    
    // Update the score state with the final calculated score
    setScore(finalScore);
    
    // Check if quiz session exists before updating
    const sessionDoc = await getDoc(doc(db, 'quizSessions', quizSessionId));
    if (!sessionDoc.exists()) {
      console.error('Quiz session not found:', quizSessionId);
      return;
    }
    
    const finalData = {
      completedAt: new Date(),
      score: finalScore,
      percentage: Math.round((finalScore / cards.length) * 100),
      totalAnswered: answeredQuestions.size + 1
    };
    
    console.log('Saving final quiz data:', finalData); // Debug log
    
    await setDoc(doc(db, 'quizSessions', quizSessionId), finalData, { merge: true });
    setQuizCompleted(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4">No Multiple Choice Cards Found</h2>
          <p className="mb-6">Create some multiple-choice flashcards first!</p>
          <button 
            onClick={() => navigate('/create')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Flashcards
          </button>
        </div>
      </div>
    );
  }

  if (quizCompleted) {
    // Calculate the final score for display
    const finalScore = Math.round((score / cards.length) * 100);
    
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Quiz Completed!</h2>
          <div className="text-4xl font-bold mb-6 text-blue-600">
            {score}/{cards.length} Correct
          </div>
          <p className="mb-6">
            You scored {finalScore}%
          </p>
          <button
            onClick={() => navigate('/quiz')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mr-4"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];
  const progress = ((currentIndex + 1) / cards.length) * 100;
  const isQuestionAnswered = answeredQuestions.has(currentCard.id);

  return (
    <div className="max-w-2xl mx-auto p-4">
      {/* Progress bar */}
      <div className="mb-6 bg-gray-200 rounded-full h-2.5">
        <div 
          className="bg-blue-600 h-2.5 rounded-full" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {/* Question counter */}
      <div className="text-right mb-2 text-gray-600">
        Question {currentIndex + 1} of {cards.length}
      </div>

      {/* Question card */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold mb-6">{currentCard.question}</h3>
        
        {/* Show warning if question was already answered */}
        {isQuestionAnswered && !showFeedback && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-yellow-800 text-sm font-medium">
                You've already answered this question. Your new answer will update your previous response.
              </span>
            </div>
          </div>
        )}
        
        <div className="space-y-3 mb-6">
          {currentCard.options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => !showFeedback && setSelectedOption(option.id)}
              disabled={showFeedback}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all
                ${selectedOption === option.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                ${showFeedback && option.id === currentCard.correctOption ? '!border-green-500 !bg-green-50' : ''}
                ${showFeedback && selectedOption === option.id && !currentCard.correctOption ? '!border-red-500 !bg-red-50' : ''}
                hover:border-blue-300`}
            >
              <span className="font-medium mr-2">
                {String.fromCharCode(65 + index)})
              </span>
              {option.text}
              {showFeedback && option.id === currentCard.correctOption && (
                <span className="float-right text-green-600">✓ Correct</span>
              )}
              {showFeedback && selectedOption === option.id && option.id !== currentCard.correctOption && (
                <span className="float-right text-red-600">✗ Incorrect</span>
              )}
            </button>
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!selectedOption || showFeedback}
          className={`w-full py-3 font-medium rounded-lg transition
            ${selectedOption && !showFeedback 
              ? isQuestionAnswered 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
        >
          {showFeedback ? 'Loading Next...' : (isQuestionAnswered ? 'Update Answer' : 'Submit Answer')}
        </button>
      </div>

      {/* Score display */}
      <div className="text-center text-lg font-medium">
        Current Score: <span className="text-blue-600">{score}</span>
        {isQuestionAnswered && !showFeedback && (
          <span className="ml-4 text-sm text-gray-500">
            (Question previously answered)
          </span>
        )}
      </div>
    </div>
  );
}

export default MultipleQuiz;